/**
 * Une action d'écran, décrite par des données : un lien (`to`) ou un geste
 * (`onClick`). L'en-tête de page, la barre d'action et les fiches la rendent
 * toujours de la même façon, ce qui garantit une seule action principale.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ActionBase {
  label: string;
  icon?: ReactNode;
  /** Libellé lu par les lecteurs d'écran quand le bouton n'affiche qu'une icône. */
  ariaLabel?: string;
}

export interface LinkAction extends ActionBase {
  to: string;
  state?: unknown;
}

export interface ButtonAction extends ActionBase {
  onClick: () => void;
  pending?: boolean;
  pendingLabel?: string;
  disabled?: boolean;
}

export type Action = LinkAction | ButtonAction;

export function isLinkAction(action: Action): action is LinkAction {
  return 'to' in action;
}

/** Classes d'un bouton du système : variante, grande taille, icône seule. */
export function buttonClass(variant: ButtonVariant, large: boolean | undefined, iconOnly: boolean, extra?: string): string {
  return [
    'btn',
    variant !== 'primary' && `btn--${variant}`,
    large && 'btn--large',
    iconOnly && 'btn--icon',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Lien présenté comme un bouton (même apparence, même cible tactile). `to`
 * ouvre une page de l'application ; `href` un lien hors de l'application
 * (`mailto:`, `tel:`, itinéraire, document), dans un nouvel onglet pour une
 * adresse web ; avec `download`, le fichier est enregistré.
 */
export function ButtonLink(props: (
  | { to: string; state?: unknown; href?: never; download?: never }
  | { href: string; to?: never; state?: never; /** Nom du fichier enregistré (image, PDF). */ download?: string }
) & {
  variant?: ButtonVariant;
  large?: boolean;
  /** Pictogramme seul : le libellé passe par `ariaLabel`. */
  iconOnly?: boolean;
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const className = buttonClass(props.variant ?? 'primary', props.large, Boolean(props.iconOnly), props.className);
  if (props.href !== undefined) {
    const web = /^https?:/i.test(props.href);
    return (
      <a
        href={props.href}
        className={className}
        aria-label={props.ariaLabel}
        download={props.download}
        {...(web && props.download === undefined ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        {props.children}
      </a>
    );
  }
  return (
    <Link to={props.to} state={props.state} className={className} aria-label={props.ariaLabel}>
      {props.children}
    </Link>
  );
}

/**
 * Rend une action : lien ou bouton, avec son état d'attente. `primary` marque
 * l'action principale de l'écran (`data-ui-primary`, vérifié par le contrat
 * d'interface : une seule visible à la fois).
 */
export function ActionButton(props: {
  action: Action;
  variant?: ButtonVariant;
  large?: boolean;
  iconOnly?: boolean;
  className?: string;
}): ReactNode {
  const { action } = props;
  const variant = props.variant ?? 'primary';
  const className = buttonClass(variant, props.large, Boolean(props.iconOnly), props.className);
  const primary = variant === 'primary' ? { 'data-ui-primary': '' } : {};
  const content = (
    <>
      {action.icon}
      {props.iconOnly ? null : <span>{!isLinkAction(action) && action.pending ? (action.pendingLabel ?? `${action.label}…`) : action.label}</span>}
    </>
  );
  if (isLinkAction(action)) {
    return (
      <Link
        to={action.to}
        state={action.state}
        className={className}
        aria-label={props.iconOnly ? (action.ariaLabel ?? action.label) : action.ariaLabel}
        {...primary}
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      disabled={action.disabled || action.pending}
      aria-busy={action.pending || undefined}
      aria-label={props.iconOnly ? (action.ariaLabel ?? action.label) : action.ariaLabel}
      onClick={(event) => {
        // Sur écran tactile, l'appui ne donne pas le focus : on le pose pour
        // qu'une fenêtre ouverte par ce bouton puisse le rendre à la fermeture.
        event.currentTarget.focus();
        action.onClick();
      }}
      {...primary}
    >
      {content}
    </button>
  );
}
